/**
 * Modal de configuração para node de delay
 * Permite definir tempo de espera antes de continuar o fluxo
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FlowNode, DelayConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

// Schema de validação
const delaySchema = z.object({
  duration: z.number().min(1, 'Duração deve ser maior que 0').max(86400, 'Duração máxima: 86400 (1 dia em segundos)'),
  unit: z.enum(['seconds', 'minutes', 'hours', 'days']),
});

interface ConfigureDelayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: DelayConfig) => void;
}

/**
 * Dialog de configuração de delay
 */
export function ConfigureDelayDialog({ open, onOpenChange, node, onSave }: ConfigureDelayDialogProps) {
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const [duration, setDuration] = useState(node?.data.config?.duration || 1);
  const [unit, setUnit] = useState<'seconds' | 'minutes' | 'hours' | 'days'>(
    node?.data.config?.unit || 'minutes'
  );
  const [showHelp, setShowHelp] = useState(false);

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      setDuration(node.data.config?.duration || 1);
      setUnit(node.data.config?.unit || 'minutes');
    }
  }, [node]);

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    try {
      const config = delaySchema.parse({ duration: Number(duration), unit });
      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as DelayConfig);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Configurar Tempo de Espera</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Campo ID do Node (somente leitura) */}
          <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

          <div className="space-y-2">
            <Label>Duração *</Label>
            <Input
              type="number"
              min="1"
              max="86400"
              placeholder="1"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label>Unidade de Tempo *</Label>
            <Select value={unit} onValueChange={(v: any) => setUnit(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="seconds">Segundos</SelectItem>
                <SelectItem value="minutes">Minutos</SelectItem>
                <SelectItem value="hours">Horas</SelectItem>
                <SelectItem value="days">Dias</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-sm font-medium mb-1">Resumo:</p>
            <p className="text-sm text-muted-foreground">
              O fluxo aguardará <span className="font-semibold text-foreground">{duration} {
                unit === 'seconds' ? 'segundo(s)' :
                unit === 'minutes' ? 'minuto(s)' :
                unit === 'hours' ? 'hora(s)' : 'dia(s)'
              }</span> antes de continuar.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="delay" />
    </>
  );
}
